import { CuisineCategories, CuisineTypes, RestaurantTypes } from '../../../../../modals/company/Constants';
import { Step } from '../../../../../modals/company/add-company-modal';
import { LeadSources, PosSystems } from '../../../../../modals/company/add-company-modal/BusinessDetails';
import { DeliveryMode } from '../../../../../services/exports/Constants';

export default {
  title: 'Restaurant Registration',
  description: 'Step {{activeStep}} of {{totalSteps}}',
  steps: {
    [Step.BasicInfo]: 'Basic Info',
    [Step.BusinessDetails]: 'Business Details',
  },
  form: {
    name: {
      label: 'Restaurant name',
      placeholder: 'Enter restaurant name',
    },
    timezone: {
      label: 'Timezone',
      placeholder: 'Search for a city or timezone',
      inference_failed: "We couldn't work out the timezone from this address — please pick it.",
    },
    google_place: {
      label: 'Selected google place: {{name}}',
    },
    is_franchise: {
      label: 'Restaurant is part of a brand',
    },
    other_platforms: {
      label: 'Current Delivery Platforms',
      description: "Select all platforms where you're currently listed",
    },
    pos_system: {
      label: 'Current POS System',
      placeholder: 'Select your POS System',
      options: {
        [PosSystems.Other]: 'Other',
      },
    },
    delivery_mode: {
      label: 'Delivery Mode',
      placeholder: 'Select delivery mode',
      options: {
        [DeliveryMode.Internal]: 'Internal (own drivers)',
        [DeliveryMode.External]: 'External (third-party)',
      },
    },
    monthly_sales_other_platforms: {
      label: 'Monthly Delivery Sales',
      description: 'Total sales across all delivery platforms',
      placeholder: 'Select sales range',
      options: {
        other: 'Other',
      },
      other: {
        placeholder: 'Enter sales amount',
      },
    },
    lead_source: {
      label: 'Lead Source',
      description: 'How did you hear about us?',
      options: {
        [LeadSources.SDR]: {
          title: 'SDR',
          description: 'Sales Development Representative outreach',
        },
        [LeadSources.Inbound]: {
          title: 'Inbound',
          description: 'Came to us through website or marketing',
        },
        [LeadSources.Referral]: {
          title: 'Referral - Existing Restaurant',
          description: 'Referred by one of our partner restaurants',
        },
        [LeadSources.SelfGeneratedCold]: {
          title: 'Self Generated - Cold',
          description: 'Found through cold outreach or prospecting',
        },
        [LeadSources.SelfGeneratedReferral]: {
          title: 'Self Generated - Referral',
          description: 'Personal network or connection',
        },
        [LeadSources.Tipster]: {
          title: 'Tipster',
          description: 'Foodamigos pays person/company X a commission for the recommendation',
        },
      },
    },
    sdr: {
      description: 'Select the SDR responsible for sourcing this lead',
    },
    has_instagram_profile: {
      label: 'Instagram Profile',
      description: 'Does your restaurant have an Instagram profile?',
      options: {
        true: 'Yes',
        false: 'No',
      },
    },
  },
  buttons: {
    back: 'Back',
    next: 'Next',
    complete_registration: 'Complete registration',
  },
  basic_information: {
    title: 'Basic Information',
    description: "Enter the restaurant's basic details and contact information",
  },
  restaurant_type: {
    title: 'Restaurant Type & Service Model',
    description: "Choose the format that best describes this restaurant's service style",
  },
  cuisine_type: {
    title: 'Food Categories & Cuisine Types',
    description: 'Select all food categories and cuisine types that apply to this restaurant',
    placeholder: 'Search cuisine types',
  },
  business_details: {
    title: 'Business Details',
    description: 'Help us understand your current operations',
  },
  restaurant_types: {
    [RestaurantTypes.FullServiceRestaurant]: {
      title: 'Full-Service Restaurant',
      description: 'Sit-down dining with table service',
    },
    [RestaurantTypes.FastCasual]: {
      title: 'Fast Casual',
      description: 'Order at counter/app, higher quality & customization than QSR, optional seating',
    },
    [RestaurantTypes.QuickServiceTakeaway]: {
      title: 'Quick Service (QSR) / Takeaway',
      description: 'Speed-first counter service; primarily takeaway',
    },
    [RestaurantTypes.CafeAndBakery]: {
      title: 'Café & Bakery',
      description: 'Coffee, pastries, light meals, breakfast/brunch',
    },
    [RestaurantTypes.DessertAndIceCream]: {
      title: 'Dessert & Ice Cream',
      description: 'Sweets-forward concepts',
    },
    [RestaurantTypes.DrinksAndBar]: {
      title: 'Drinks & Bar',
      description: 'Beverage-led venues; food optional; alcohol delivery subject to local rules',
    },
    [RestaurantTypes.DeliveryOnlyOrGhostKitchen]: {
      title: 'Delivery-Only / Ghost Kitchen',
      description: 'No storefront; virtual brands and cloud kitchens',
    },
  },
  cuisine_types: {
    [CuisineTypes.Pizza]: 'Pizza',
    [CuisineTypes.Burgers]: 'Burgers',
    [CuisineTypes.ChickenFried]: 'Chicken Fried',
    [CuisineTypes.Wings]: 'Wings',
    [CuisineTypes.KebabDoner]: 'Kebab/Doner',
    [CuisineTypes.Sandwiches]: 'Sandwiches',
    [CuisineTypes.Wraps]: 'Wraps',
    [CuisineTypes.Salads]: 'Salads',
    [CuisineTypes.Healthy]: 'Healthy',
    [CuisineTypes.Bowls]: 'Bowls',
    [CuisineTypes.Vegan]: 'Vegan',
    [CuisineTypes.Vegetarian]: 'Vegetarian',
    [CuisineTypes.GlutenFree]: 'Gluten Free',
    [CuisineTypes.Halal]: 'Halal',

    [CuisineTypes.Sushi]: 'Sushi',
    [CuisineTypes.Japanese]: 'Japanese',
    [CuisineTypes.Ramen]: 'Ramen',
    [CuisineTypes.UdonSoba]: 'Udon/Soba',
    [CuisineTypes.Korean]: 'Korean',
    [CuisineTypes.KoreanFriedChicken]: 'Korean Fried Chicken',
    [CuisineTypes.Chinese]: 'Chinese',
    [CuisineTypes.Sichuan]: 'Sichuan',
    [CuisineTypes.CantoneseDimSum]: 'Cantonese/Dim Sum',
    [CuisineTypes.Thai]: 'Thai',
    [CuisineTypes.Vietnamese]: 'Vietnamese',
    [CuisineTypes.Malaysian]: 'Malaysian',
    [CuisineTypes.Indonesian]: 'Indonesian',
    [CuisineTypes.Filipino]: 'Filipino',
    [CuisineTypes.Poke]: 'Poke',
    [CuisineTypes.HotPot]: 'Hot Pot',
    [CuisineTypes.DumplingsGyozaBao]: 'Dumplings/Gyoza/Bao',

    [CuisineTypes.Indian]: 'Indian',
    [CuisineTypes.Biryani]: 'Biryani',
    [CuisineTypes.Pakistani]: 'Pakistani',
    [CuisineTypes.SriLankan]: 'Sri Lankan',
    [CuisineTypes.Nepalese]: 'Nepalese',

    [CuisineTypes.Turkish]: 'Turkish',
    [CuisineTypes.Lebanese]: 'Lebanese',
    [CuisineTypes.Syrian]: 'Syrian',
    [CuisineTypes.Israeli]: 'Israeli',
    [CuisineTypes.Egyptian]: 'Egyptian',
    [CuisineTypes.PersianIranian]: 'Persian/Iranian',
    [CuisineTypes.Iraqi]: 'Iraqi',
    [CuisineTypes.Afghan]: 'Afghan',
    [CuisineTypes.ShawarmaFalafel]: 'Shawarma/Falafel',
    [CuisineTypes.MezzeGrill]: 'Mezze/Grill',
    [CuisineTypes.Greek]: 'Greek',
    [CuisineTypes.Mediterranean]: 'Mediterranean',

    [CuisineTypes.Italian]: 'Italian',
    [CuisineTypes.Pasta]: 'Pasta',
    [CuisineTypes.French]: 'French',
    [CuisineTypes.SpanishTapas]: 'Spanish/Tapas',
    [CuisineTypes.Portuguese]: 'Portuguese',
    [CuisineTypes.German]: 'German',
    [CuisineTypes.Austrian]: 'Austrian',
    [CuisineTypes.Balkan]: 'Balkan',
    [CuisineTypes.Hungarian]: 'Hungarian',
    [CuisineTypes.Polish]: 'Polish',
    [CuisineTypes.Czech]: 'Czech',
    [CuisineTypes.Georgian]: 'Georgian',

    [CuisineTypes.Mexican]: 'Mexican',
    [CuisineTypes.TexMex]: 'Tex-Mex',
    [CuisineTypes.Peruvian]: 'Peruvian',
    [CuisineTypes.Brazilian]: 'Brazilian',
    [CuisineTypes.Argentinian]: 'Argentinian',
    [CuisineTypes.Caribbean]: 'Caribbean',
    [CuisineTypes.BBQAmerican]: 'BBQ/American',
    [CuisineTypes.Steakhouse]: 'Steakhouse',
    [CuisineTypes.AmericanGeneral]: 'American (General)',
    [CuisineTypes.HotDogs]: 'Hot Dogs',

    [CuisineTypes.Seafood]: 'Seafood',
    [CuisineTypes.FishAndChips]: 'Fish and Chips',
    [CuisineTypes.SteamedFish]: 'Steamed Fish',

    [CuisineTypes.BreakfastBrunch]: 'Breakfast/Brunch',
    [CuisineTypes.Bagels]: 'Bagels',
    [CuisineTypes.Bakery]: 'Bakery',
    [CuisineTypes.PastryDessert]: 'Pastry/Dessert',
    [CuisineTypes.WafflesCrepes]: 'Waffles/Crepes',
    [CuisineTypes.IceCreamGelato]: 'Ice Cream/Gelato',
    [CuisineTypes.Donuts]: 'Donuts',
    [CuisineTypes.Cakes]: 'Cakes',

    [CuisineTypes.CoffeeCafe]: 'Coffee/Cafe',
    [CuisineTypes.BubbleTea]: 'Bubble Tea',
    [CuisineTypes.JuiceSmoothies]: 'Juice/Smoothies',
    [CuisineTypes.Milkshakes]: 'Milkshakes',
    [CuisineTypes.BarPub]: 'Bar/Pub',
    [CuisineTypes.WineBar]: 'Wine Bar',
    [CuisineTypes.Cocktails]: 'Cocktails',

    [CuisineTypes.TapasMezeSmallPlates]: 'Tapas/Mezze/Small Plates',
    [CuisineTypes.Noodles]: 'Noodles',
    [CuisineTypes.Soup]: 'Soup',
    [CuisineTypes.Schnitzel]: 'Schnitzel',
    [CuisineTypes.SoulFood]: 'Soul Food',
    [CuisineTypes.ComfortFood]: 'Comfort Food',
    [CuisineTypes.StreetFood]: 'Street Food',
    [CuisineTypes.OrganicBio]: 'Organic/Bio',
    [CuisineTypes.KidsMenu]: 'Kids Menu',
  },
  cuisine_categories: {
    [CuisineCategories.TopGeneral]: 'Top/General',
    [CuisineCategories.AsianPacific]: 'Asian/Pacific',
    [CuisineCategories.SouthAsian]: 'South Asian',
    [CuisineCategories.MiddleEastMediterranean]: 'Middle East/Mediterranean',
    [CuisineCategories.Europe]: 'Europe',
    [CuisineCategories.Americas]: 'Americas',
    [CuisineCategories.SeafoodSpecialty]: 'Seafood Specialty',
    [CuisineCategories.BreakfastBakerySweets]: 'Breakfast Bakery/Sweets',
    [CuisineCategories.BeverageLed]: 'Beverage Led',
    [CuisineCategories.OtherConcepts]: 'Other Concepts',
  },
};
